import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateContext } from '../../../../StateProvider';
import { fetchSuggestedFriends, addFriend } from '../../../../Utilities/api';
import './Friends.css';

import arrowIcon from '../../../../../assets/Utilities/arrowIcon.png';
import blueArrowIcon from '../../../../../assets/Utilities/blueArrowIcon.png';
import tokenIcon from '../../../../../assets/Utilities/tokenIcon.png';

import profilePlaceholder1 from '../../../../../assets/Dashboard/Profile/profilePlaceholder-3.png';
import profilePlaceholder2 from '../../../../../assets/Dashboard/Profile/profilePlaceholder-4.png';
import profilePlaceholder3 from '../../../../../assets/Dashboard/Profile/profilePlaceholder-5.png';

import { XButton } from '../../../../Utilities/components/Buttons';

const Friends = () => {
  const navigate = useNavigate();
  const { user, refreshUserData } = useContext(StateContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [friendsList, setFriendsList] = useState([]);
  const [suggestedFriends, setSuggestedFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingUid, setAddingUid] = useState('');
  const [error, setError] = useState('');

  useEffect(function () {
    async function getSuggestedFriends() {
      try {
        if (!user || !user.uid) {
          setLoading(false);
          return;
        }
        const friends = await fetchSuggestedFriends(user.uid);
        // Expecting array of { uid, name, petName, image }
        const arr = Array.isArray(friends) ? friends : [];
        setSuggestedFriends(arr);
        setFriendsList(arr);
      } catch (err) {
        console.error('Error fetching suggested friends:', err);
        setError('Could not load suggestions.');
      } finally {
        setLoading(false);
      }
    }
    getSuggestedFriends();
  }, [user]);

  function handleSearch() {
    const term = (searchTerm || '').trim().toLowerCase();
    if (term === '') {
      setFriendsList(suggestedFriends);
      return;
    }
    const filtered = suggestedFriends.filter(function (friend) {
      const name = friend && friend.name ? String(friend.name).toLowerCase() : '';
      const pet  = friend && friend.petName ? String(friend.petName).toLowerCase() : '';
      return name.indexOf(term) !== -1 || pet.indexOf(term) !== -1;
    });
    setFriendsList(filtered);
  }

  async function handleAddFriend(friendUid) {
    if (!user || !user.uid) return;
    if (!friendUid) return;

    try {
      setAddingUid(friendUid);
      const resp = await addFriend({ userUid: user.uid, friendUid: friendUid });
      if (resp && resp.success) {
        // Optimistic UI: remove from suggestions and list
        const nextSuggestions = suggestedFriends.filter(function (f) { return f.uid !== friendUid; });
        const nextList = friendsList.filter(function (f) { return f.uid !== friendUid; });
        setSuggestedFriends(nextSuggestions);
        setFriendsList(nextList);
        // Optional: refresh user data so Friends list reflects new connection
        if (typeof refreshUserData === 'function' && user.uid) {
          refreshUserData(user.uid);
        }
      } else {
        setError(resp && resp.message ? resp.message : 'Failed to add friend.');
      }
    } catch (err) {
      console.error('Add friend error:', err);
      setError('Failed to add friend.');
    } finally {
      setAddingUid('');
    }
  }

  function onKeyDown(e) {
    if (e && e.key === 'Enter') handleSearch();
  }

  return (
    <div>
      <div className='friends-backdrop'>
        <XButton />
        <div className="friends-container">
          <div className='friends-search-container'>
            <input
              type="text"
              placeholder="Search Friends"
              value={searchTerm}
              onChange={function (e) { setSearchTerm(e.target.value); }}
              onKeyDown={onKeyDown}
              className='friends-search-input'
            />
            <button onClick={handleSearch} className='friends-search-button'>Search</button>
          </div>
        </div>

        <div className="friends-container">
          <div className='friends-card-container-one'>
            <h3 className='friends-card-header'>
              Friend Suggestions
              <img className='friends-arrow-image' src={arrowIcon} alt="See suggestions" />
            </h3>
            <p className='friends-card-paragraph'>Friendships your pet made while playing at Pawsible.</p>

            {loading ? (
              <p className='friends-suggestion-list'>Finding pack mates…</p>
            ) : error ? (
              <p className='friends-suggestion-list'>{error}</p>
            ) : friendsList.length > 0 ? (
              <div className='friends-suggestions-grid'>
                {friendsList.map(function (friend) {
                  const key = friend && friend.uid ? friend.uid : Math.random().toString(36);
                  const img = friend && friend.image ? friend.image : profilePlaceholder1;
                  const title = friend && friend.name ? friend.name : 'Pawsible Member';
                  const pet = friend && friend.petName ? friend.petName : 'Pet';

                  return (
                    <div key={key} className='friends-suggestion-card'>
                      <img src={img} className='friends-card-token-image' alt={title} />
                      <div className='friends-suggestion-meta'>
                        <div className='friends-suggestion-name'>{title}</div>
                        <div className='friends-suggestion-pet'>{pet}</div>
                      </div>
                      <button
                        className='friends-add-button'
                        disabled={addingUid === friend.uid}
                        onClick={function () { handleAddFriend(friend.uid); }}
                      >
                        {addingUid === friend.uid ? 'Adding…' : 'Add Friend'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className='friends-suggestion-list'>Try daycare to find new friends!</p>
            )}

            <hr className='friends-breakline' />
            <p className='friends-card-history-paragraph'>View your history</p>
          </div>
        </div>

        <div className='friends-container'>
          <div className='friends-card-container-two' onClick={function () { navigate('/store/deals/invite'); }}>
            <h3 className='friends-refer-header'>Refer & earn!</h3>
            <p className='friends-refer-paragraph'>Invite friends to Pawsible, earn rewards up to $425.</p>
            <p className='friends-refer-learn-paragraph'>
              Learn more
              <img className='wallet-arrow-image' src={blueArrowIcon} alt="Learn more" />
            </p>
          </div>
        </div>
      </div>

      <div className='friends-container-two'>
        <h3 className='friends-list-header'>Friends</h3>
        {user && user.userFriends && user.userFriends.length > 0 ? (
          user.userFriends.map(function (friend) {
            const img = friend && friend.image ? friend.image : profilePlaceholder2;
            const pet = friend && friend.petName ? friend.petName : 'Pet';
            const key = friend && friend.id ? friend.id : Math.random().toString(36);
            return (
              <div key={key} className="friends-list-friend">
                <img className='friends-list-image' src={img} alt={pet} />
                <p className='friends-list-name'>{pet}</p>
              </div>
            );
          })
        ) : (
          <p className='friends-list-empty'>Start adding friends from the suggestions!</p>
        )}
      </div>
    </div>
  );
};

export default Friends;
